import SyncIcon from '@mui/icons-material/Sync'
import { Button, Chip, CircularProgress, IconButton, Stack } from "@mui/material"
import { Localized, useLocalization } from "@fluent/react"
import { Dispatch, SetStateAction } from "react"
import { DateFilterMenu } from "../../../documents/utils/DateFilterMenu"
import { StandardFilterMenu } from "../../../documents/utils/StandardFilterMenu"
import { ValidationProcessListFilters } from "../validationProcessFilterHelper"

export const defaultValidationProcessFilters: ValidationProcessListFilters = {
    date: { from: "all", to: "all" },
    statusOptions: [
        {
            uniqueName: "I",
            name: "_W trakcie",
            translationCode: "project-process-inprogress",
            selected: false,
        },
        {
            uniqueName: "H",
            name: "_Wstrzymany",
            translationCode: "project-process-onhold",
            selected: false,
        },
        {
            uniqueName: "F",
            name: "_Zakończony",
            translationCode: "project-process-finished",
            selected: false,
        },
        {
            uniqueName: "C",
            name: "_Anulowany",
            translationCode: "project-process-cancelled",
            selected: false,
        },
    ],
    myRoleOptions: [
        {
            uniqueName: "IN",
            name: "_Jestem w zespole",
            translationCode: "filters-im-in-the-team",
            checkOther: true,
            selected: false,
        },
        {
            uniqueName: "M",
            name: "_Menedżer projektu",
            translationCode: "project-role-manager",
            selected: false,
        },
        {
            uniqueName: "C",
            name: "_Współpracownik projektu",
            translationCode: "project-role-collaborator",
            selected: false,
        },
        {
            uniqueName: "O",
            name: "_Obserwator",
            translationCode: "project-role-observer",
            selected: false,
        },
    ],
}

interface ValidationProcessFilterPanelProps {
    filters: ValidationProcessListFilters
    onFilterChange: Dispatch<SetStateAction<ValidationProcessListFilters>>
    reload: () => void
    isLoading?: boolean
}

export const ValidationProcessFilterPanel = ({ filters, onFilterChange, reload, isLoading = false }: ValidationProcessFilterPanelProps) => {
    const { l10n } = useLocalization()

    const handleSubmit = (date) => {
        onFilterChange(prevFilters => ({
            ...prevFilters,
            date: { from: date.dateFrom, to: date.dateTo },
        }))
    }

    const updateOption = (type: "status" | "myRole", name: string) => {
        if (type === "status") {
            onFilterChange(prevFilters => ({
                ...prevFilters,
                statusOptions: prevFilters.statusOptions.map(item =>
                    item.uniqueName === name ? { ...item, selected: !item.selected } : item
                ),
            }))
            return
        }

        if (name === "IN") {
            const selectedInOption = filters.myRoleOptions.find(option => option.uniqueName === "IN")?.selected
            onFilterChange(prevFilters => ({
                ...prevFilters,
                myRoleOptions: prevFilters.myRoleOptions.map(item => ({
                    ...item,
                    selected: !selectedInOption,
                })),
            }))
            return
        }

        onFilterChange(prevFilters => ({
            ...prevFilters,
            myRoleOptions: prevFilters.myRoleOptions.map(item =>
                item.uniqueName === name ? { ...item, selected: !item.selected } : item
            ),
        }))
    }

    const handleClearDateFilterClick = () => {
        onFilterChange(prevFilters => ({
            ...prevFilters,
            date: { from: "all", to: "all" },
        }))
    }

    const handleClearFiltersClick = () => {
        onFilterChange(prevFilters => ({
            date: { from: "all", to: "all" },
            statusOptions: prevFilters.statusOptions.map(item => ({
                ...item,
                selected: false,
            })),
            myRoleOptions: prevFilters.myRoleOptions.map(item => ({
                ...item,
                selected: false,
            })),
        }))
    }

    const hasFilters = filters.statusOptions.some(option => option.selected)
        || filters.myRoleOptions.some(option => option.selected)
        || filters.date.from !== "all"

    return (
        <Stack sx={{ paddingBottom: 1 }}>
            <Stack direction="row" spacing={1} flexWrap="wrap">
                <DateFilterMenu
                    allowAllOption
                    defaultOption="all"
                    currentOption={filters.date.from === "all" ? "all" : undefined}
                    onSubmit={handleSubmit}
                />
                <StandardFilterMenu
                    label={l10n.getString("status", null, "Status")}
                    multiple
                    options={filters.statusOptions}
                    onUpdate={(name) => updateOption("status", name)}
                />
                {filters.myRoleOptions.length > 0 && (
                    <StandardFilterMenu
                        label={l10n.getString("process-team", null, "Zespół")}
                        multiple
                        options={filters.myRoleOptions}
                        onUpdate={(name) => updateOption("myRole", name)}
                    />
                )}
                <IconButton onClick={reload} disabled={isLoading}>
                    {isLoading ? <CircularProgress size={18} /> : <SyncIcon />}
                </IconButton>
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
                {filters.date.from !== "all" ? (
                    <Chip
                        label={new Date(filters.date.from).toLocaleDateString() + " - " + new Date(filters.date.to).toLocaleDateString()}
                        size="small"
                        onDelete={handleClearDateFilterClick}
                    />
                ) : null}
                {filters.statusOptions.filter(option => option.selected).map((option) => (
                    <Chip
                        key={`status-${option.uniqueName}`}
                        label={<span><strong>Status: </strong>{option.translationCode ? <Localized id={option.translationCode}>{option.name}</Localized> : option.name}</span>}
                        size="small"
                        onDelete={() => updateOption("status", option.uniqueName)}
                    />
                ))}
                {filters.myRoleOptions.filter(option => option.selected).map((option) => (
                    <Chip
                        key={`role-${option.uniqueName}`}
                        label={<span><strong><Localized id="process-team">Zespół</Localized>: </strong>{option.translationCode ? <Localized id={option.translationCode}>{option.name}</Localized> : option.name}</span>}
                        size="small"
                        onDelete={() => updateOption("myRole", option.uniqueName)}
                    />
                ))}
                {hasFilters ? (
                    <Button size="small" variant="text" onClick={handleClearFiltersClick}>
                        <Localized id="filters-clear-all-filters">_Wyczyść filtry</Localized>
                    </Button>
                ) : null}
            </Stack>
        </Stack>
    )
}